<template>
  <div class="addressForm-page">
    <div class="form-box">
      <van-form @submit="onSubmit">
        <van-cell-group>
          <van-field
            v-model="formData.receiverName"
            label="收货人"
            placeholder="收货人"
            label-class="color-black"
            :rules="[{ required: true, message: '请输入收货人' }]"
          />
          <van-field
            v-model.trim="formData.receiverPhone"
            label="手机号"
            placeholder="手机号"
            :maxlength="11"
            label-class="color-black"
            :rules="[
              { validator: validatorPhone, message: '请输入正确的手机号码' },
            ]"
          />
          <van-field
            v-model="formData.areaValues"
            is-link
            readonly
            name="area"
            label="收货地址"
            placeholder="小区/大厦/学校"
            label-class="color-black"
            :rules="[{ required: true, message: '请选择收货地址' }]"
            @click="showArea = true"
          />

          <van-field
            v-model="formData.detailAddress"
            rows="1"
            autosize
            label="详细地址"
            type="textarea"
            label-class="color-black"
            placeholder="门牌号等(例如：10栋1001号)"
            :rules="[{ required: true, message: '请输入详细地址' }]"
          />
        </van-cell-group>

        <div class="btn-row">
          <van-button
            round
            block
            color="#0996B5"
            :loading="btnDisabled"
            native-type="submit"
          >
            领取
          </van-button>
        </div>
      </van-form>
    </div>

    <van-popup v-model:show="showArea" round position="bottom">
      <van-cascader
        v-model="cascaderValue"
        title="选择地址"
        :options="options"
        active-color="#0996B5"
        @close="showArea = false"
        @finish="onFinish"
        @change="onChange"
      />
    </van-popup>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  luyuanXibRegionList,
  luyuanXibEnhanceReceive,
} from '@/api/luyuan'

const route = useRoute()
const router = useRouter()

// 校验函数返回 true 表示校验通过，false 表示不通过
const validatorPhone = (val) => /1\d{10}/.test(val)

const formData = reactive({
  receiverName: '', // 收货人
  receiverPhone: '', // 收货人手机号

  areaValues: '', // 地区选择展示值
  provinceCode: '', // 省份代码
  provinceName: '', // 省份名称
  cityCode: '', // 城市代码
  cityName: '', // 城市名称
  countyCode: '', // 区县代码
  countyName: '', // 区县名称
  streetCode: '', // 街道代码
  streetName: '', // 街道名称

  detailAddress: '',
})

const btnDisabled = ref(false)
const showArea = ref(false)
const cascaderValue = ref('')
const options = ref([])

const getRegionCode = (item = {}) =>
  String(
    item.code ??
      item.value ??
      item.regionCode ??
      item.provinceCode ??
      item.cityCode ??
      item.countyCode ??
      item.streetCode ??
      ''
  )

const getRegionName = (item = {}) =>
  item.name ??
  item.text ??
  item.regionName ??
  item.provinceName ??
  item.cityName ??
  item.countyName ??
  item.streetName ??
  ''

const mapToCascaderOptions = (list = [], level = 0) =>
  list
    .map((item) => {
      const value = getRegionCode(item)
      const text = getRegionName(item)
      if (!value || !text) return null
      return {
        text,
        value,
        // 四级地址：省(0)->市(1)->区县(2)->街道(3)
        // 前三级都需要预置 children，点击后继续按 code 拉下级
        children: level < 3 ? [] : undefined,
        loaded: false,
      }
    })
    .filter(Boolean)

const fetchListRegion = async (parentCode = 1, level = 0) => {
  try {
    const { code, data } = await luyuanXibRegionList({ parentCode })
    if (code !== 0) return []
    return mapToCascaderOptions(Array.isArray(data) ? data : [], level)
  } catch (error) {
    console.log('fetchListRegion error', error)
    return []
  }
}

const onChange = async ({ value, selectedOptions = [], tabIndex = 0 }) => {
  const current = selectedOptions[tabIndex]
  if (!current) return
  // 四级地址：省(0)->市(1)->区县(2)->街道(3)，末级不再请求
  if (tabIndex >= 3) return
  // 避免重复请求已加载数据
  if (
    current.loaded &&
    Array.isArray(current.children) &&
    current.children.length
  ) {
    return
  }

  const children = await fetchListRegion(value, tabIndex + 1)
  current.children = children.length ? children : undefined
  current.loaded = true
  // 触发 cascader 视图更新
  options.value = [...options.value]
}

const onFinish = ({ selectedOptions }) => {
  showArea.value = false
  if (!selectedOptions?.length) return
  formData.areaValues = selectedOptions.map((option) => option.text).join('/')
  const [province, city, county, street] = selectedOptions
  formData.provinceCode = province?.value || ''
  formData.provinceName = province?.text || ''
  formData.cityCode = city?.value || ''
  formData.cityName = city?.text || ''
  formData.countyCode = county?.value || ''
  formData.countyName = county?.text || ''
  formData.streetCode = street?.value || ''
  formData.streetName = street?.text || ''
}

onMounted(async () => {
  options.value = await fetchListRegion(1, 0)
})

// 提交
const onSubmit = async () => {
  const { gradeNum, openId, appId, equityType } = route.query
  const obj = {
    gradeNum,
    equityType: +equityType, // 权益类型：4-实物商品1 5-实物商品2
    receiverName: formData.receiverName,
    receiverPhone: formData.receiverPhone,
    provinceCode: formData.provinceCode,
    provinceName: formData.provinceName,
    cityCode: formData.cityCode,
    cityName: formData.cityName,
    countyCode: formData.countyCode,
    countyName: formData.countyName,
    streetCode: formData.streetCode,
    streetName: formData.streetName,
    detailAddress: formData.detailAddress,
  }
  btnDisabled.value = true
  try {
    const { data } = await luyuanXibEnhanceReceive(obj)
    // result"发放结果：1-成功，0-失败"
    if (data.result === 1) {
      router.replace({
        path: '/luyuanPromotionGiftPhysicalDetail',
        query: { id: data.detailsId }
      })
    } else {
      router.replace({ path: '/luyuanPromotionGiftFailDetail' })
    }
  } catch (err) {
    console.log('err', err)
  } finally {
    btnDisabled.value = false
  }
}
</script>

<style lang="less" scoped>
.addressForm-page {
  min-height: 100vh;
  background: #f6f6f6;
  padding: 15px;
  color: #131821;
  box-sizing: border-box;

  .form-box {
    border-radius: 5px;
    overflow: hidden;
  }

  .btn-row {
    position: fixed;
    bottom: 50px;
    left: 0;
    right: 0;
    padding-inline: 16px;
  }

  :deep(.color-black) {
    color: #131821 !important;
  }
}
</style>
